import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface MigrationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  photoIds: string[];
  currentTier?: string;
  targetTier: 'INSTANT' | 'STANDARD' | 'ARCHIVE';
}

// S3 pricing (per GB per month)
const PRICING = {
  INSTANT: 0.023,
  STANDARD: 0.0125,
  ARCHIVE: 0.00099
};

// Estimated average photo size (MB)
const AVG_PHOTO_SIZE_MB = 25;

const MigrationDialog: React.FC<MigrationDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  photoIds,
  currentTier = 'INSTANT',
  targetTier
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const photoCount = photoIds.length;
  const estimatedSizeGB = (photoCount * AVG_PHOTO_SIZE_MB) / 1024;

  // Calculate cost impact
  const currentCost = estimatedSizeGB * PRICING[currentTier.toUpperCase() as keyof typeof PRICING] || 0;
  const newCost = estimatedSizeGB * PRICING[targetTier];
  const savings = currentCost - newCost;

  const getTierConfig = (tier: string) => {
    switch (tier.toUpperCase()) {
      case 'INSTANT':
        return { icon: '⚡', label: 'Instant', color: 'green', retrieval: 'Instant access' };
      case 'STANDARD':
        return { icon: '📦', label: 'Standard', color: 'yellow', retrieval: '<1 second retrieval' };
      case 'ARCHIVE':
        return { icon: '🗄️', label: 'Archive', color: 'blue', retrieval: '3-5 hours retrieval' };
      default:
        return { icon: '⚡', label: 'Instant', color: 'green', retrieval: 'Instant access' };
    }
  };

  const targetConfig = getTierConfig(targetTier);
  const isArchiving = targetTier === 'ARCHIVE';

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await onConfirm();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl max-w-lg w-full p-6"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
              Move Photos to {targetConfig.label}
            </h3>
            <button
              onClick={onClose}
              className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200"
              disabled={isSubmitting}
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="space-y-4">
            <div>
              <p className="text-neutral-700 dark:text-neutral-300">
                You're about to move <span className="font-semibold">{photoCount} photo{photoCount !== 1 ? 's' : ''}</span> to {targetConfig.icon} {targetConfig.label} storage.
              </p>
            </div>

            {/* Cost Impact */}
            <div className="bg-neutral-50 dark:bg-neutral-900 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-neutral-600 dark:text-neutral-400">Current cost:</span>
                <span className="font-medium text-neutral-900 dark:text-white">${currentCost.toFixed(2)}/month</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-neutral-600 dark:text-neutral-400">New cost:</span>
                <span className="font-medium text-neutral-900 dark:text-white">${newCost.toFixed(2)}/month</span>
              </div>
              <div className="pt-2 border-t border-neutral-200 dark:border-neutral-700">
                <div className="flex justify-between">
                  <span className={`font-semibold ${savings > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {savings > 0 ? 'Savings:' : 'Additional cost:'}
                  </span>
                  <span className={`font-bold ${savings > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    ${Math.abs(savings).toFixed(2)}/month
                  </span>
                </div>
              </div>
            </div>

            {/* Retrieval Warning for Archive */}
            {isArchiving && (
              <div className="flex gap-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-yellow-800 dark:text-yellow-200 mb-1">
                    Important: Archive Retrieval Time
                  </p>
                  <p className="text-yellow-700 dark:text-yellow-300">
                    Photos moved to Archive require 3-5 hours to retrieve if you need to access them later. Only move photos you don't need immediate access to.
                  </p>
                </div>
              </div>
            )}

            {/* Estimated Size */}
            <div className="text-sm text-neutral-600 dark:text-neutral-400">
              <p>Estimated size: {estimatedSizeGB.toFixed(2)} GB</p>
              <p>Retrieval time: {targetConfig.retrieval}</p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-6">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 text-neutral-700 dark:text-neutral-300 bg-neutral-100 dark:bg-neutral-700 hover:bg-neutral-200 dark:hover:bg-neutral-600 rounded-md font-medium transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              className={`flex-1 px-4 py-2 text-white rounded-md font-medium transition-colors ${
                savings > 0
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-primary-600 hover:bg-primary-700'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Moving...' : `Confirm Migration`}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default MigrationDialog;
